"use client";

import React from "react";
import { Activity, ArrowRight, ChevronRight, CircleCheck as CheckCircle2, Cpu, Layers, ShieldCheck, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
export default function MethodEngineeringHero() {
  const shouldReduceMotion = useReducedMotion();
  const focusAreas = [{
    id: 1,
    title: "Spectroscopic Feasibility",
    description: "Wavelength range verification, signal-to-noise ratio testing, and matrix interference mitigation across complex formulations.",
    icon: Activity,
    tag: "Stage 01"
  }, {
    id: 2,
    title: "Optical Interface Calibration",
    description: "Probe geometry adaptation, flow cell pathlength optimization, and transmission stabilization for inline immersion setups.",
    icon: Layers,
    tag: "Stage 02"
  }, {
    id: 3,
    title: "Chemometric Workflow Readiness",
    description: "Pre-processing pipeline design, PLS/PCA calibration modeling, and validation protocols aligned with GMP standards.",
    icon: Cpu,
    tag: "Stage 03"
  }];
  const highlights = ["NIR, Raman & UV-Vis Modalities", "GMP & ICH Q2(R2) Compliant", "Ireland & UK On-Site Support"];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="methodengineeringhero" data-nav="light" className="relative w-full overflow-hidden bg-background py-16 md:py-24 lg:py-28">
      {/* Background Decorative Accents */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-32 right-1/4 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute top-1/2 -left-20 h-80 w-80 rounded-full bg-secondary blur-3xl" />
      </div>

      <div className="page-container">
        <motion.div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-8" variants={containerVariants} initial="hidden" animate="visible">
          {/* Left Column (7 cols) */}
          <motion.div className="flex flex-col items-start lg:col-span-7" variants={itemVariants}>
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-6 flex items-center text-xs text-muted-foreground sm:text-sm">
              <ol className="flex flex-wrap items-center gap-1.5 break-words">
                <li className="inline-flex items-center gap-1.5">
                  <Link to="/HomePage" className="transition-colors hover:text-foreground">
                    Home
                  </Link>
                </li>
                <li aria-hidden="true" className="text-muted-foreground/60">
                  <ChevronRight className="size-3.5" />
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <Link to="/ServicesPage" className="transition-colors hover:text-foreground">
                    Services
                  </Link>
                </li>
                <li aria-hidden="true" className="text-muted-foreground/60">
                  <ChevronRight className="size-3.5" />
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <span className="font-medium text-foreground" aria-current="page">
                    Application Development
                  </span>
                </li>
              </ol>
            </nav>

            {/* Category Badge */}
            <Badge variant="secondary" className="mb-6 gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold tracking-wide uppercase border border-border/80">
              <Sparkles className="size-3.5 text-primary" />
              <span>Application Engineering &amp; Method Design</span>
            </Badge>

            {/* H1 Heading */}
            <h1 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-[2.75rem] lg:leading-[1.15]">
              Tailored Analytical Methods Engineered for Regulated Process
              Workflows
            </h1>

            {/* Supporting Copy */}
            <p className="mt-6 text-base text-muted-foreground sm:text-lg sm:leading-relaxed">
              We engineer quantitative and qualitative NIR, Raman, and UV-Vis
              spectroscopic methods for pharmaceutical, chemical, and bioprocess
              manufacturing teams across Ireland and the UK. From initial optical
              feasibility through to chemometric calibration and routine process
              validation, our specialists deliver robust, inspection-ready
              measurement workflows.
            </p>

            {/* Key Value Highlights */}
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2">
              {highlights.map((highlight, i) => <div key={highlight} data-index={i} className="flex items-center gap-2 text-xs font-medium text-foreground sm:text-sm">
                  <ShieldCheck className="size-4 text-primary shrink-0" />
                  <span>{highlight}</span>
                </div>)}
            </div>

            {/* Dual CTAs */}
            <div className="mt-8 flex flex-col gap-3.5 sm:flex-row sm:items-center">
              <Button asChild size="lg" className="gap-2 font-medium">
                <Link to="/DiscussYourApplicationPage">
                  <span>Discuss Method Development</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="font-medium">
                <Link to="/ProcessAnalyticalTechnologyPage">
                  <span>Explore Development Process</span>
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column (5 cols) - Technical Card */}
          <motion.div className="lg:col-span-5" variants={itemVariants}>
            <Card className="border-border/80 bg-card shadow-lg ring-1 ring-border/40">
              <CardHeader className="border-b border-border/60 pb-5">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <CardTitle className="text-xl font-bold text-card-foreground">
                      Engineering Scope
                    </CardTitle>
                    <CardDescription className="mt-1 text-xs">
                      Core validation pillars for process-integrated analytics
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="border-primary/30 bg-primary/5 text-primary text-[11px] font-semibold">
                    3-Pillar Framework
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="flex flex-col divide-y divide-border/60 pt-2">
                {focusAreas.map((area, index) => {
                const IconComp = area.icon;
                return <div key={area.id} data-index={index} className="group flex items-start gap-4 py-4.5 first:pt-4 last:pb-2 transition-colors hover:bg-muted/30 -mx-6 px-6">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                        <IconComp className="size-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="text-sm font-semibold text-card-foreground">
                            {area.title}
                          </h3>
                          <span className="text-[11px] font-mono font-medium text-muted-foreground uppercase">
                            {area.tag}
                          </span>
                        </div>
                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                          {area.description}
                        </p>
                      </div>
                    </div>;
              })}
              </CardContent>

              <div className="px-6 pb-6 pt-2">
                <div className="flex items-center justify-between rounded-lg bg-secondary/80 p-3 text-xs text-secondary-foreground border border-border/50">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span className="font-medium">
                      Tailored for PAT &amp; Laboratory Scale
                    </span>
                  </div>
                  <Link to="/FeasibilityStudiesPage" className="font-semibold text-primary hover:underline underline-offset-4">
                    Learn more &rarr;
                  </Link>
                </div>
              </div>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}